import React, { createContext, useEffect, useState } from "react";
import { ThemeProvider, CssBaseline } from "@mui/material";
import GlobalStyles from "@mui/material/GlobalStyles";
import routes from "./routes";
import { useLocation, useRoutes } from "react-router-dom";
import { lightTheme, styles } from "./theme";
import { HelpDialog } from "./views/shared/HelpDialog";
import { HelpContent } from "./views/shared/help";
import {
  CONSTANTS as SHARED_CONSTANTS,
  GoogleAnalytics,
} from "@theatticusapp/atticus-shared-components";
import { EXCLUDE_PATH_PAGE_VIEW, MAINTENANCE_ENABLED, META_ROBOTS_VALUE } from "./constants";
import { NotAuthedLayout } from "./layouts/NotAuthedLayout";
import { MaintenanceView } from "./views/maintenance/MaintenanceView";
import { Helmet } from "react-helmet";


export interface AppContextInterface {
  helpContent?: HelpContent;
  setHelpContent: (helpContent: HelpContent) => void;
}

export const AppContext = createContext<AppContextInterface>({
  helpContent: undefined,
  setHelpContent: () => undefined,
});

export const App = (): JSX.Element => {
  const routing = useRoutes(routes);
  const location = useLocation();
  useEffect(() => {
    // add a GA event for screens loaded via react router
    //  screens that are the first to be hit after transition from the old app should be excluded
    //  the previous should change once the app has been fully transitioned away from the legacy Vue app
    if (!EXCLUDE_PATH_PAGE_VIEW.includes(location.pathname)) {
      GoogleAnalytics.pushEvent(); // with page defaults
    }
  }, [location]);

  const [helpContent, setHelpContent] = useState<HelpContent | undefined>(
    undefined
  );
  // documentation: https://github.com/TheAtticusApp/atticus-app/wiki/Maintenance-mode
  const isMaintenanceBypassed =
    window.localStorage.getItem(SHARED_CONSTANTS.STORAGE_KEYS.BYPASS) ===
    "true";
  const isMaintenanceEnabled = MAINTENANCE_ENABLED && !isMaintenanceBypassed;

  return (
    <AppContext.Provider
      value={{
        helpContent,
        setHelpContent,
      }}
    >
      <ThemeProvider theme={lightTheme}>
        <GlobalStyles styles={styles} />
        <CssBaseline />
        <Helmet>
          <meta name="robots" content={META_ROBOTS_VALUE} />
        </Helmet>
        {/*{authRequired && <SessionMonitor />}*/}
        <HelpDialog
          open={helpContent !== undefined}
          title={helpContent?.title}
          content={helpContent?.content}
          confirmText="Ok"
          onConfirm={() => setHelpContent(undefined)}
        />
        {!isMaintenanceEnabled ? (
          routing
        ) : (
          <NotAuthedLayout forceTopBar={true} centerLogo={true}>
            <MaintenanceView />
          </NotAuthedLayout>
        )}
      </ThemeProvider>
    </AppContext.Provider>
  );
};
